@if (form(); as formGroup) {
  <form class="flex w-full flex-col gap-8" [formGroup]="formGroup">
    <!-- Teeth Section -->
    <div formGroupName="teeth">
      <h2 class="!font-medium">Teeth Information</h2>

      <div class="mb-4 flex flex-col gap-4" formArrayName="injuries">
        @for (
          control of formGroup.toothInjuryControls;
          track idx;
          let idx = $index;
          let first = $first
        ) {
          <div
            class="flex flex-col gap-4 rounded bg-gray-50 p-4"
            [formGroupName]="idx"
          >
            @if (first === false) {
              <div class="flex justify-end">
                <button
                  mat-stroked-button
                  color="warn"
                  type="button"
                  [disabled]="disabled()"
                  (click)="formGroup.removeToothInjuryForm($event, idx)"
                >
                  Remove
                </button>
              </div>
            }

            <mat-form-field class="w-full">
              <mat-label>Tooth Number</mat-label>
              <mat-select formControlName="toothNumber" required>
                @for (number of toothNumberOptions; track number) {
                  <mat-option [value]="number">{{ number }}</mat-option>
                }
              </mat-select>
            </mat-form-field>

            <mat-form-field class="w-full">
              <mat-label>Injury Classification</mat-label>
              <mat-select
                formControlName="injuryClassification"
                multiple
                required
              >
                @for (
                  option of toothInjuryClassificationOptions;
                  track option.code
                ) {
                  <mat-option [value]="option.label">
                    {{ option.code | splitCamel }}
                  </mat-option>
                }
              </mat-select>
              @if (control.injuryClassificationRequiredError) {
                <mat-error>This field is required</mat-error>
              }
            </mat-form-field>

            <mat-form-field class="w-full">
              <mat-label>Pre-Accident Condition</mat-label>
              <mat-select
                formControlName="preAccidentCondition"
                multiple
                required
              >
                @for (
                  option of toothPreAccidentConditionOptions;
                  track option.code
                ) {
                  <mat-option [value]="option.label">{{
                    option.label
                  }}</mat-option>
                }
              </mat-select>
              @if (control.preAccidentConditionRequiredError) {
                <mat-error>This field is required</mat-error>
              }
            </mat-form-field>
          </div>
        }

        <div class="flex justify-end">
          <button
            mat-stroked-button
            color="primary"
            type="button"
            [disabled]="disabled()"
            (click)="formGroup.addToothInjuryForm($event)"
          >
            <mat-icon>add</mat-icon>
            Add Tooth Injury
          </button>
        </div>
      </div>

      <mat-form-field class="w-full">
        <mat-label>Additional Injury Comments</mat-label>
        <textarea
          matInput
          formControlName="additionalInjuryComment"
          rows="3"
        ></textarea>
        <mat-hint align="end">Optional (max 500 characters)</mat-hint>
      </mat-form-field>
    </div>

    <mat-divider />

    <!-- Prosthesis Section -->
    <div formGroupName="prosthesis">
      <h2 class="!font-medium">Prosthesis Information</h2>

      <div>
        <p class="font-bold">Was a prosthesis worn at the time of injury? *</p>
        <mat-radio-group
          class="mb-2 flex flex-col"
          formControlName="wornAtTimeOfInjury"
          required
        >
          @for (option of booleanOptions; track option.code) {
            <mat-radio-button class="mr-4" [value]="option.label">
              {{ booleanDisplay[option.code] }}
            </mat-radio-button>
          }
        </mat-radio-group>
        @if (formGroup.prosthesis.wornAtTimeOfInjuryRequiredError) {
          <mat-error>This field is required</mat-error>
        }
      </div>

      @if (formGroup.prosthesis.displayProsthesisType) {
        <div class="mt-4">
          <mat-form-field class="w-full">
            <mat-label>Prosthesis Type</mat-label>
            <mat-select formControlName="prosthesisType" multiple required>
              @for (option of prosthesisTypes; track option.code) {
                <mat-option [value]="option.label">{{
                  option.label
                }}</mat-option>
              }
            </mat-select>
            <mat-hint>Select all applicable types</mat-hint>
            @if (formGroup.prosthesis.prosthesisTypeRequiredError) {
              <mat-error>
                This field is required when prosthesis was worn
              </mat-error>
            }
          </mat-form-field>
        </div>

        <div class="mt-4">
          <mat-form-field class="w-full">
            <mat-label>Teeth on Partial Denture</mat-label>
            <mat-select formControlName="teethOnPartialDenture" multiple>
              @for (number of toothNumberOptions; track number) {
                <mat-option [value]="number">{{ number }}</mat-option>
              }
            </mat-select>
            <mat-hint>Select all applicable teeth</mat-hint>
          </mat-form-field>
        </div>
      }
    </div>

    <mat-divider />

    <!-- Soft Tissue Section -->
    <div formGroupName="softTissue">
      <h2 class="!font-medium">Soft Tissue Information</h2>

      <div class="mb-4" formGroupName="gingiva">
        <p class="font-bold">Gingiva</p>
        <mat-form-field class="w-full">
          <mat-label>How</mat-label>
          <mat-select formControlName="how" multiple>
            @for (option of softTissueHowOptions; track option.code) {
              <mat-option [value]="option.label">{{ option.label }}</mat-option>
            }
          </mat-select>
          <mat-hint>Select all applicable types</mat-hint>
        </mat-form-field>

        <mat-form-field class="mt-4 w-full">
          <mat-label>Position in Mouth</mat-label>
          <input matInput formControlName="positionInMouth" />
          @if (formGroup.softTissue.gingiva.hasHowSelection) {
            <mat-hint align="end">Max 100 characters</mat-hint>
          } @else {
            <mat-hint>Requires a 'How' selection</mat-hint>
          }
          @if (formGroup.softTissue.gingiva.positionInMouthRequiredError) {
            <mat-error>This field is required</mat-error>
          }
        </mat-form-field>
      </div>

      <div class="mb-4" formGroupName="mucosa">
        <p class="font-bold">Mucosa</p>
        <mat-form-field class="w-full">
          <mat-label>How</mat-label>
          <mat-select formControlName="how" multiple>
            @for (option of softTissueHowOptions; track option.code) {
              <mat-option [value]="option.label">{{ option.label }}</mat-option>
            }
          </mat-select>
          <mat-hint>Select all applicable types</mat-hint>
        </mat-form-field>

        <mat-form-field class="mt-4 w-full">
          <mat-label>Position in Mouth</mat-label>
          <input matInput formControlName="positionInMouth" />
          @if (formGroup.softTissue.mucosa.hasHowSelection) {
            <mat-hint align="end">Max 100 characters</mat-hint>
          } @else {
            <mat-hint>Requires a 'How' selection</mat-hint>
          }
          @if (formGroup.softTissue.mucosa.positionInMouthRequiredError) {
            <mat-error>This field is required</mat-error>
          }
        </mat-form-field>
      </div>

      <div class="mb-4" formGroupName="lip">
        <p class="font-bold">Lip</p>
        <mat-form-field class="w-full">
          <mat-label>How</mat-label>
          <mat-select formControlName="how" multiple>
            @for (option of softTissueHowOptions; track option.code) {
              <mat-option [value]="option.label">{{ option.label }}</mat-option>
            }
          </mat-select>
          <mat-hint>Select all applicable types</mat-hint>
        </mat-form-field>

        <mat-form-field class="mt-4 w-full">
          <mat-label>Position in Mouth</mat-label>
          <input matInput formControlName="positionInMouth" />
          @if (formGroup.softTissue.lip.hasHowSelection) {
            <mat-hint align="end">Max 100 characters</mat-hint>
          } @else {
            <mat-hint>Requires a 'How' selection</mat-hint>
          }
          @if (formGroup.softTissue.lip.positionInMouthRequiredError) {
            <mat-error>This field is required</mat-error>
          }
        </mat-form-field>
      </div>

      <div>
        <mat-form-field class="w-full">
          <mat-label>Degloving Injury</mat-label>
          <mat-select formControlName="deglovingInjury" multiple>
            @for (option of deglovingInjuryOptions; track option.code) {
              <mat-option [value]="option.label">{{ option.label }}</mat-option>
            }
          </mat-select>
          <mat-hint>Select all applicable</mat-hint>
        </mat-form-field>
      </div>
    </div>

    <mat-divider />

    <!-- Jaw, Alveolus, TMJ Section -->
    <div formGroupName="jawAlveolusTmj">
      <h2 class="!font-medium">Jaw, Alveolus, TMJ Information</h2>

      <div class="mb-4">
        <mat-form-field class="w-full">
          <mat-label>Alveolus Injuries</mat-label>
          <mat-select formControlName="alveolusInjuries" multiple>
            @for (option of alveolusInjuryOptions; track option.code) {
              <mat-option [value]="option.label">{{ option.label }}</mat-option>
            }
          </mat-select>
          <mat-hint>Select all applicable</mat-hint>
        </mat-form-field>
      </div>

      <div class="mb-4">
        <mat-form-field class="w-full">
          <mat-label>Teeth Involved</mat-label>
          <mat-select formControlName="teethInvolved" multiple>
            @for (number of toothNumberOptions; track number) {
              <mat-option [value]="number">{{ number }}</mat-option>
            }
          </mat-select>
          <mat-hint>Select all applicable teeth</mat-hint>
          @if (formGroup.jawAlveolusTmj.teethInvolvedRequiredError) {
            <mat-error>This field is required</mat-error>
          }
        </mat-form-field>
      </div>

      <div class="mb-4">
        <mat-form-field class="w-full">
          <mat-label>Jaw Injuries</mat-label>
          <mat-select formControlName="jawInjuries" multiple>
            @for (option of jawInjuryOptions; track option.code) {
              <mat-option [value]="option.label">{{ option.label }}</mat-option>
            }
          </mat-select>
          <mat-hint>Select all applicable</mat-hint>
        </mat-form-field>
      </div>

      <div class="mb-4">
        <mat-form-field class="w-full">
          <mat-label>Type/Position</mat-label>
          <input matInput formControlName="typePosition" />
          <mat-hint align="end">Optional (max 100 characters)</mat-hint>
          @if (formGroup.jawAlveolusTmj.typePositionRequiredError) {
            <mat-error>This field is required</mat-error>
          }
        </mat-form-field>
      </div>

      <div class="mb-4">
        <mat-form-field class="w-full">
          <mat-label>TMJ Injuries</mat-label>
          <mat-select formControlName="tmjInjuries" multiple>
            @for (option of tmjInjuryOptions; track option.code) {
              <mat-option [value]="option.label">{{ option.label }}</mat-option>
            }
          </mat-select>
          <mat-hint>Select all applicable</mat-hint>
        </mat-form-field>
      </div>

      <div>
        <mat-form-field class="w-full">
          <mat-label>Specific Injury</mat-label>
          <input matInput formControlName="specificInjury" />
          <mat-hint align="end">Optional (max 100 characters)</mat-hint>
          @if (formGroup.jawAlveolusTmj.specificInjuryRequiredError) {
            <mat-error>This field is required</mat-error>
          }
        </mat-form-field>
      </div>
    </div>

    <mat-divider />

    <!-- Other Section -->
    <div formGroupName="other">
      <h2 class="!font-medium">Other Dental Injury Information</h2>

      <div class="mb-4">
        <p class="font-bold">Permanent teeth missing prior to accident? *</p>
        <mat-radio-group
          class="mb-2 flex flex-col"
          formControlName="permanentTeethMissingPriorToAccident"
          required
        >
          @for (option of booleanOptions; track option.code) {
            <mat-radio-button class="mr-4" [value]="option.label">
              {{ booleanDisplay[option.code] }}
            </mat-radio-button>
          }
        </mat-radio-group>
        @if (
          formGroup.other.permanentTeethMissingPriorToAccidentRequiredError
        ) {
          <mat-error>This field is required</mat-error>
        }
      </div>

      @if (formGroup.other.displayTeethMissing) {
        <div class="mb-4">
          <mat-form-field class="w-full">
            <mat-label>Teeth Missing</mat-label>
            <mat-select formControlName="teethMissing" multiple required>
              @for (number of toothNumberOptions; track number) {
                <mat-option [value]="number">{{ number }}</mat-option>
              }
            </mat-select>
            <mat-hint>Select all applicable teeth</mat-hint>
            @if (formGroup.other.teethMissingRequiredError) {
              <mat-error>
                This field is required when teeth are missing
              </mat-error>
            }
          </mat-form-field>
        </div>
      }

      <div class="mb-4">
        <mat-form-field class="w-full">
          <mat-label>Oral Hygiene</mat-label>
          <mat-select formControlName="oralHygiene" required>
            @for (option of conditionOptions; track option.code) {
              <mat-option [value]="option.label">{{ option.label }}</mat-option>
            }
          </mat-select>
          @if (formGroup.other.oralHygieneRequiredError) {
            <mat-error>This field is required</mat-error>
          }
        </mat-form-field>
      </div>

      <div class="mb-4">
        <mat-form-field class="w-full">
          <mat-label>Periodontal Condition</mat-label>
          <mat-select formControlName="periodontalCondition" required>
            @for (option of conditionOptions; track option.code) {
              <mat-option [value]="option.label">{{ option.label }}</mat-option>
            }
          </mat-select>
          @if (formGroup.other.periodontalConditionRequiredError) {
            <mat-error>This field is required</mat-error>
          }
        </mat-form-field>
      </div>

      <div class="mb-4">
        <mat-form-field class="w-full">
          <mat-label>Caries Activity in Mouth</mat-label>
          <mat-select formControlName="cariesActivityInMouth" required>
            @for (option of cariesActivityOptions; track option.code) {
              <mat-option [value]="option.label">{{ option.label }}</mat-option>
            }
          </mat-select>
          @if (formGroup.other.cariesActivityInMouthRequiredError) {
            <mat-error>This field is required</mat-error>
          }
        </mat-form-field>
      </div>

      <div class="mb-4">
        <p class="font-bold">Were radiographs taken? *</p>
        <mat-radio-group
          class="mb-2 flex flex-col"
          formControlName="radiographsTaken"
          required
        >
          @for (option of booleanOptions; track option.code) {
            <mat-radio-button class="mr-4" [value]="option.label">
              {{ booleanDisplay[option.code] }}
            </mat-radio-button>
          }
        </mat-radio-group>
        @if (formGroup.other.radiographsTakenRequiredError) {
          <mat-error>This field is required</mat-error>
        }
      </div>

      @if (formGroup.other.displayRadiographsTaken) {
        <div class="mb-4">
          <mat-form-field class="w-full">
            <mat-label>Radiographs Type</mat-label>
            <input matInput formControlName="radiographsType" required />
            <mat-hint align="end">Max 100 characters</mat-hint>
            @if (formGroup.other.radiographsTypeRequiredError) {
              <mat-error
                >This field is required when radiographs were taken</mat-error
              >
            }
          </mat-form-field>
        </div>
      }

      <div>
        <p class="font-bold">Is there photographic record of injury? *</p>
        <mat-radio-group
          class="flex flex-col"
          formControlName="photographicRecordOfInjury"
          required
        >
          @for (option of booleanOptions; track option.code) {
            <mat-radio-button class="mr-4" [value]="option.label">
              {{ booleanDisplay[option.code] }}
            </mat-radio-button>
          }
        </mat-radio-group>
        @if (formGroup.other.photographicRecordOfInjuryRequiredError) {
          <mat-error>This field is required</mat-error>
        }
      </div>
    </div>
  </form>
}
